import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config";

interface ListeningQuestion {
  answer: string;
  marks: number;
}

interface WritingQuestion {
  answer: string;
  marks: number;
}

interface ScreeningQuestions {
  listening: ListeningQuestion[];
  writing: WritingQuestion;
}

const Evaluation = () => {
  const navigate = useNavigate();
  const [playingAudio, setPlayingAudio] = useState<number | null>(null);
  const [marks, setMarks] = useState({
    listening: [0, 0, 0, 0, 0, 0, 0],
    writing: 0,
  });
  const [evaluationData, setEvaluationData] =
    useState<ScreeningQuestions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    const fetchScreeningQuestions = async () => {
      try {
        const candidateId = sessionStorage.getItem("candidateId");
        if (!candidateId) {
          throw new Error("No candidate ID found in session");
        }

        const response = await fetch(
          `${API_BASE_URL}/screening-questions/${candidateId}`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch screening questions");
        }
        const data = await response.json();
        if (data && data.length > 0) {
          // Check if the candidate has completed the entire test
          const testData = data[0];
          if (
            !testData.listening ||
            testData.listening.length === 0 ||
            !testData.writing ||
            !testData.writing.answer
          ) {
            throw new Error("Candidate has not completed the entire test");
          }

          setEvaluationData(testData);
          // Initialize marks from the API data
          setMarks({
            listening: testData.listening.map(
              (q: ListeningQuestion) => q.marks
            ),
            writing: testData.writing.marks,
          });
        } else {
          throw new Error("No test data found for this candidate");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchScreeningQuestions();
  }, []);

  // Define max marks for each listening question
  const getMaxMarksForQuestion = (index: number): number => {
    return index < 4 ? 0.5 : 1; // First 4 questions: 0.5 marks, last 3: 1 mark
  };

  // Define scoring options for each question
  const getScoringOptions = (index: number): number[] => {
    return index < 4 ? [0, 0.5] : [0, 1]; // First 4: 0 or 0.5, last 3: 0 or 1
  };

  const handlePlayAudio = (index: number): void => {
    if (playingAudio === index) {
      setPlayingAudio(null);
    } else {
      setPlayingAudio(index);
    }
  };

  const updateListeningMark = (index: number, mark: number): void => {
    const newMarks = [...marks.listening];
    newMarks[index] = mark;
    setMarks({ ...marks, listening: newMarks });
  };

  const updateWritingMark = (mark: number): void => {
    setMarks({ ...marks, writing: mark });
  };

  const getTotalScore = (): number => {
    const listeningTotal = marks.listening.reduce((sum, mark) => sum + mark, 0);
    return listeningTotal + marks.writing;
  };

  const getMaxScore = (): number => {
    const listeningMax =
      evaluationData?.listening.reduce((sum, _, index) => {
        return sum + getMaxMarksForQuestion(index);
      }, 0) || 0;
    return listeningMax + 5; // 5 for writing
  };

  const handleSubmitEvaluation = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      const examId = sessionStorage.getItem("examId");
      if (!examId) {
        throw new Error("No exam ID found");
      }

      // Format the data according to the required structure
      const formattedData = {
        listening:
          evaluationData?.listening.map((item, index) => ({
            answer: item.answer,
            marks: marks.listening[index],
          })) || [],
        writing: {
          answer: evaluationData?.writing.answer || "",
          marks: marks.writing,
        },
      };

      const response = await fetch(
        `${API_BASE_URL}/screening-questions/${examId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formattedData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit evaluation");
      }

      setSubmitSuccess(true);
      // Navigate to trainer dashboard after successful submission
      setTimeout(() => {
        navigate("/trainer/dashboard");
      }, 2000);
    } catch (err) {
      console.error("Submit error:", err);
      alert("Failed to submit evaluation. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  interface ScoreButtonProps {
    score: number;
    isActive: boolean;
    onClick: (score: number) => void;
  }

  const ScoreButton: React.FC<ScoreButtonProps> = ({
    score,
    isActive,
    onClick,
  }) => (
    <button
      onClick={() => onClick(score)}
      className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
        isActive
          ? "bg-red-500 text-white shadow-md"
          : "bg-gray-100 text-gray-700 hover:bg-red-100 hover:text-red-700"
      }`}
    >
      {score}
    </button>
  );

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6 bg-red-50 min-h-screen flex items-center justify-center">
        <div className="text-xl text-red-600">Loading evaluation data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6 bg-red-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <div className="text-2xl font-bold text-red-600 mb-2">
            Evaluation Not Available
          </div>
          <div className="text-lg text-red-500 mb-4">
            {error === "Candidate has not completed the entire test"
              ? "This candidate has not taken the complete screening test yet."
              : `Error: ${error}`}
          </div>
          {error === "Candidate has not completed the entire test" && (
            <div className="text-sm text-gray-600 max-w-md">
              The candidate must complete both the listening and writing
              sections before evaluation can be performed.
            </div>
          )}
          <button
            onClick={() => navigate("/trainer/dashboard")}
            className="mt-6 px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!evaluationData) {
    return (
      <div className="max-w-6xl mx-auto p-6 bg-red-50 min-h-screen flex items-center justify-center">
        <div className="text-xl text-red-600">No evaluation data found</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 bg-red-50 min-h-screen">
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <span className="text-red-500">✓</span>
            Evaluation Dashboard
          </h1>
          <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-3 rounded-lg">
            <div className="text-sm opacity-90">Total Score</div>
            <div className="text-2xl font-bold">
              {getTotalScore()}/{getMaxScore()}
            </div>
          </div>
        </div>

        {/* Listening Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="text-red-500">🔊</span>
            Listening Evaluation ({evaluationData.listening.length} Questions)
          </h2>

          <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
            <p className="text-sm text-red-800">
              <strong>Scoring:</strong> Questions 1-4: 0.5 marks each |
              Questions 5-7: 1 mark each
            </p>
          </div>

          <div className="grid gap-4">
            {evaluationData.listening.map((item, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow hover:border-red-200"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-medium text-gray-800">
                    Question {index + 1}
                    <span className="text-sm text-gray-500 ml-2">
                      (Max: {getMaxMarksForQuestion(index)} marks)
                    </span>
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Score:</span>
                    <span className="font-bold text-lg text-red-600">
                      {marks.listening[index]}/{getMaxMarksForQuestion(index)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handlePlayAudio(index)}
                      className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      {playingAudio === index ? "⏸️" : "▶️"}
                      {playingAudio === index ? "Pause" : "Play"} Audio
                    </button>

                    <audio
                      src={item.answer}
                      onEnded={() => setPlayingAudio(null)}
                      ref={(audio) => {
                        if (audio && playingAudio === index) {
                          audio.play();
                        } else if (audio && playingAudio !== index) {
                          audio.pause();
                        }
                      }}
                      className="hidden"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Marks:</span>
                    {getScoringOptions(index).map((score) => (
                      <ScoreButton
                        key={score}
                        score={score}
                        isActive={marks.listening[index] === score}
                        onClick={(mark) => updateListeningMark(index, mark)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Writing Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="text-red-500">📝</span>
            Writing Evaluation
          </h2>

          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow hover:border-red-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-800">
                Writing Submission
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Score:</span>
                <span className="font-bold text-lg text-red-600">
                  {marks.writing}/5
                </span>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <img
                  src={evaluationData.writing.answer}
                  alt="Writing submission"
                  className="w-full rounded-lg border border-gray-300 shadow-sm hover:shadow-md transition-shadow cursor-pointer hover:border-red-300"
                  onClick={() =>
                    window.open(evaluationData.writing.answer, "_blank")
                  }
                />
                <p className="text-sm text-gray-500 mt-2 text-center">
                  Click image to view full size
                </p>
              </div>

              <div className="flex flex-col justify-center">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assign Marks:
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[0, 1, 2, 3, 4, 5].map((score) => (
                      <ScoreButton
                        key={score}
                        score={score}
                        isActive={marks.writing === score}
                        onClick={updateWritingMark}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="mb-8 bg-gradient-to-r from-red-50 to-pink-50 rounded-lg p-6 border border-red-100">
          <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="text-red-500">⭐</span>
            Evaluation Summary
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {marks.listening.reduce((sum, mark) => sum + mark, 0)}
              </div>
              <div className="text-sm text-gray-600">Listening Score</div>
              <div className="text-xs text-gray-500">
                (Max:{" "}
                {evaluationData.listening.reduce(
                  (sum, _, index) => sum + getMaxMarksForQuestion(index),
                  0
                )}
                )
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {marks.writing}
              </div>
              <div className="text-sm text-gray-600">Writing Score</div>
              <div className="text-xs text-gray-500">(Max: 5)</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {getTotalScore()}
              </div>
              <div className="text-sm text-gray-600">Total Score</div>
              <div className="text-xs text-gray-500">
                (Max: {getMaxScore()})
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-center">
          <button
            onClick={handleSubmitEvaluation}
            disabled={isSubmitting}
            className={`px-8 py-4 rounded-lg text-white font-semibold text-lg transition-all transform hover:scale-105 ${
              isSubmitting
                ? "bg-gray-400 cursor-not-allowed"
                : submitSuccess
                ? "bg-green-500 hover:bg-green-600"
                : "bg-red-500 hover:bg-red-600 shadow-lg hover:shadow-xl"
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Submitting...
              </div>
            ) : submitSuccess ? (
              <div className="flex items-center gap-2">
                <span>✓</span>
                Submitted Successfully!
              </div>
            ) : (
              "Submit Evaluation"
            )}
          </button>
        </div>

        {submitSuccess && (
          <div className="mt-4 p-4 bg-green-100 border border-green-300 rounded-lg text-center">
            <p className="text-green-800 font-medium">
              Evaluation submitted successfully! Total Score: {getTotalScore()}/
              {getMaxScore()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Evaluation;
